/**
 * The Gun-backed `SyncTransport`. The only place in the health domain that talks
 * to the graph.
 *
 * Kept apart from `syncEngine.ts` so the engine stays testable against a fake
 * graph — the two failures that matter most there, a peer that never acknowledges
 * and two devices converging on one day, are unit tests only because the engine
 * never sees a Gun type. This file is the part that cannot be tested that way, so
 * it is deliberately thin: no decisions, no state, no merge logic. Anything
 * interesting belongs on the other side of the interface.
 *
 * ## Where records are written
 *
 * `myRoot().get('e').get(soul)` — the SEA user's own graph. Write-protected by
 * Gun (only the key holder can write), world-READABLE, which is why the record is
 * sealed and the soul is an HMAC. `NAMESPACE-MAP.md` records this as the only
 * public node Thana writes, and a second one must be added to that table or it
 * does not ship.
 */
import { gunInstance, gunOn, gunPutWithRetry, isPeerConnected, myRoot, sessionPub, GUN_ROOT } from '@shared/data/gun'
import type { SyncTransport } from './syncEngine'

const DEBUG_SYNC = String(import.meta.env?.VITE_SYNC_DEBUG ?? '') === '1'

function log(...args: any[]) {
  if (DEBUG_SYNC) {
    console.log(...args)
  }
}

/** The bucket under the user's root. Fixed, meaningless, and short on purpose. */
const BUCKET = 'e'

/**
 * The narrow slice of a Gun node this file uses.
 *
 * The layer types its chainables as `any` — a deliberate choice recorded in
 * `tsconfig.gun.json`, because typing Gun's chain faithfully is a project in
 * itself. Thana's own code is held to stricter settings, so the `any` is confined
 * to exactly one cast in `entryNode` below rather than spreading an unsafe chain
 * through every call site.
 */
interface GraphNode {
  get(key: string): GraphNode
}

/** The field the envelope is stored under, inside the node named by the soul. */
const FIELD = 's'

/**
 * The node one day's record lives at: `~<pub>/e/<soul>`.
 *
 * The single place the graph path is constructed, so the claim in
 * `NAMESPACE-MAP.md` — one public node, no date, no alias, no device id — has one
 * place to be true rather than one per caller.
 */
function entryNode(soul: string): GraphNode {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- myRoot() is `any` by the layer's design; this is the one cast that contains it.
  const root: GraphNode = myRoot()
  return root.get(BUCKET).get(soul)
}

function entryReadNode(soul: string): GraphNode {
  const mine = sessionPub()
  if (!mine) {
    throw new Error('No active session to read entries.')
  }
  // Subscribing to the public soul ~<pub> forces Gun to fetch updates from the network relays
  return gunInstance().get(`~${mine}`).get(GUN_ROOT).get(BUCKET).get(soul)
}

export function createGunSyncTransport(): SyncTransport {
  return {
    async write(soul, envelope) {
      log("[gunSyncTransport] write called. soul:", soul, "envelope size:", envelope.length)
      /*
       * Written as `{ s: envelope }` rather than as a bare string.
       *
       * Gun stores a node, not a scalar, at a path like this — putting a raw
       * string works but leaves nowhere to add anything later without changing
       * the shape of records already in relays. One named field costs nothing now
       * and avoids a migration across a graph nobody controls.
       */
      const result = await gunPutWithRetry(entryNode(soul), { [FIELD]: envelope })
      log("[gunSyncTransport] write outcome. ok:", result.ok, "confirmedByPeer:", (result as any).confirmedByPeer, "error:", (result as any).error)

      /*
       * `confirmedByPeer` passes through untouched, and must keep doing so.
       *
       * The layer sets it true only for a relay's acknowledgement, never for the
       * local store's — that distinction is the entire basis of FR-507, and
       * defaulting it to `result.ok` here would reintroduce the bug the engine is
       * built to prevent, one layer lower where its tests cannot see it.
       */
      return {
        ok: result.ok,
        confirmedByPeer: result.ok ? result.confirmedByPeer : false,
      }
    },

    subscribe(soul, onValue) {
      log("[gunSyncTransport] subscribe called. soul:", soul)
      return gunOn(entryReadNode(soul), (data: unknown) => {
        log("[gunSyncTransport] subscribe update received for soul:", soul, "data exists:", !!data)
        // Gun fires for a node before its fields arrive, handing back an object
        // carrying only its metadata. Passing that on would have the engine try
        // to open `undefined` as an envelope on every subscription.
        if (data === null || typeof data !== 'object') return
        const envelope = (data as Record<string, unknown>)[FIELD]
        if (typeof envelope !== 'string' || envelope === '') return
        onValue(envelope)
      })
    },

    isPeerConnected,
  }
}
